import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % heroSlides.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + heroSlides.length) % heroSlides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="推荐轮播">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={slide.bgImage}
          alt={slide.title}
          className={`w-full h-full object-cover transition-all duration-700 ${isTransitioning ? 'scale-105 opacity-80' : 'scale-100 opacity-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-background-50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full w-full px-4 md:px-6 lg:px-10 flex items-center">
        <div className="w-full max-w-3xl">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium mb-4 whitespace-nowrap">
            {slide.tag}
          </span>
          <h2 className={`text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading mb-3 transition-all duration-500 ${isTransitioning ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}`}>
            {slide.title}
          </h2>
          <p className={`text-lg md:text-xl text-white/90 mb-2 transition-all duration-500 delay-75 ${isTransitioning ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}`}>
            {slide.subtitle}
          </p>
          <p className={`text-sm md:text-base text-white/70 mb-6 transition-all duration-500 delay-100 ${isTransitioning ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}`}>
            {slide.description}
          </p>
          <div className={`flex items-center gap-4 transition-all duration-500 delay-150 ${isTransitioning ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}`}>
            <Link
              to={`/article/${slide.movieId}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              立即观看
            </Link>
            <div className="flex items-center gap-1.5 text-white/80">
              <i className="ri-star-fill text-accent-500 text-sm"></i>
              <span className="text-sm font-medium">{slide.rating}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all z-20 cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all z-20 cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`rounded-full transition-all cursor-pointer ${
              i === current
                ? 'w-6 h-2 bg-primary-500'
                : 'w-2 h-2 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`第${i + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}